/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { Feature } from 'ol';
import type { StyleLike } from 'ol/style/Style';
import React from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import type { layerTypes, CoreAppStore } from '@opengeoweb/store';
import { layerActions, drawingToolSelectors } from '@opengeoweb/store';
import type {
  DrawModeExitCallback,
  DrawModeValue,
  HoverSelect,
} from '@opengeoweb/webmap-react';
import {
  OpenLayersFeatureLayer,
  OpenLayersMapDraw,
} from '@opengeoweb/webmap-react';

interface OpenLayersFeatureLayerConnectProps {
  layer: layerTypes.ReduxLayer;
  layerId?: string;
  zIndex: number;
  style: StyleLike;
  hoverSelect?: HoverSelect;
}

export const OpenLayersFeatureLayerConnect: React.FC<
  OpenLayersFeatureLayerConnectProps
> = ({
  layer,
  layerId,
  zIndex,
  style,
  hoverSelect,
}: OpenLayersFeatureLayerConnectProps) => {
  const store = useStore<CoreAppStore>();
  const dispatch = useDispatch();
  const activeDrawToolId = useSelector(
    drawingToolSelectors.getActiveDrawToolId,
  );
  const activeDrawMode = useSelector((store: CoreAppStore) =>
    drawingToolSelectors.getActiveDrawMode(store, activeDrawToolId),
  );
  const shouldAllowMultipleShapes = useSelector((store: CoreAppStore) =>
    drawingToolSelectors.getShouldAllowMultipleShapes(store, activeDrawToolId),
  );
  const geoJSONIntersectionLayerId = useSelector((store: CoreAppStore) =>
    drawingToolSelectors.getGeoJSONIntersectionLayerId(store, activeDrawToolId),
  );
  const geoJSONIntersectionBoundsLayerId = useSelector((store: CoreAppStore) =>
    drawingToolSelectors.getGeoJSONIntersectionBoundsLayerId(
      store,
      activeDrawToolId,
    ),
  );

  const onFeatureClick = React.useCallback(
    (feature: Feature[]) => {
      const previouslySelectedFeatureIndex = layer.selectedFeatureIndex;
      const selectedFeatureIndex = layer.geojson!.features.findIndex(
        (feat) => feature[0] && feat.id === feature[0].getId(),
      );
      if (selectedFeatureIndex !== -1) {
        dispatch(
          layerActions.setSelectedFeature({
            layerId: layerId!,
            selectedFeatureIndex,
          }),
        );

        dispatch(
          layerActions.toggleFeatureMode({
            layerId: layerId!,
            isInEditMode: true,
            drawMode: '',
          }),
        );

        dispatch(
          layerActions.updateFeatureProperties({
            layerId: layerId!,
            properties: {
              highlightSelectedFeature: true,
            },
            selectedFeatureIndex,
          }),
        );

        if (previouslySelectedFeatureIndex !== undefined) {
          dispatch(
            layerActions.updateFeatureProperties({
              layerId: layerId!,
              properties: {
                highlightSelectedFeature: false,
              },
              selectedFeatureIndex: previouslySelectedFeatureIndex,
            }),
          );
        }
      }
    },
    [dispatch, layer.geojson, layer.selectedFeatureIndex, layerId],
  );

  const onNoFeatureClick = React.useCallback((): void => {
    if (activeDrawToolId) {
      // do not disable edit mode when a drawing tool is active
      return;
    }
    dispatch(
      layerActions.toggleFeatureMode({
        layerId: layerId!,
        isInEditMode: false,
        drawMode: '',
      }),
    );
  }, [activeDrawToolId, dispatch, layerId]);

  const onViewOnlyFeatureClick = React.useCallback(
    (feature: Feature[]): void => {
      if (activeDrawToolId) {
        // do not select another feature when a drawing tool is active
        return;
      }
      onFeatureClick(feature);
    },
    [activeDrawToolId, onFeatureClick],
  );

  const updateFeature = React.useCallback(
    (geojson: GeoJSON.FeatureCollection, reason: string): void => {
      if (!layerId) {
        window.console.error('updateFeature without layerId');
        return;
      }

      dispatch(
        layerActions.updateFeature({
          geojson,
          reason,
          layerId,
          shouldAllowMultipleShapes,
          geoJSONIntersectionLayerId,
          geoJSONIntersectionBoundsLayerId,
          selectionType: activeDrawMode?.selectionType,
        }),
      );
    },
    [
      layerId,
      dispatch,
      shouldAllowMultipleShapes,
      geoJSONIntersectionLayerId,
      geoJSONIntersectionBoundsLayerId,
      activeDrawMode?.selectionType,
    ],
  );

  const exitFeatureDrawMode = React.useCallback(
    (reason: DrawModeExitCallback): void => {
      if (!layerId) {
        window.console.error('exitFeatureDrawMode without layerId');
        return;
      }
      const activeDrawToolId = drawingToolSelectors.getActiveDrawToolId(
        store.getState(),
      );

      dispatch(
        layerActions.exitFeatureDrawMode({
          reason,
          layerId,
          shouldAllowMultipleShapes:
            drawingToolSelectors.getShouldAllowMultipleShapes(
              store.getState(),
              activeDrawToolId,
            ),
        }),
      );
    },
    [dispatch, store, layerId],
  );

  return (
    <>
      <OpenLayersFeatureLayer
        clickOnFeature={
          layer.isSelectable
            ? {
                onClick: onFeatureClick,
                hitTolerance: 5,
              }
            : undefined
        }
        featureCollection={!layer.isInEditMode ? layer.geojson : undefined}
        hoverSelect={hoverSelect}
        opacity={layer.opacity}
        style={style}
        visible={layer.enabled}
        zIndex={zIndex}
      />
      <OpenLayersMapDraw
        drawMode={layer.drawMode as DrawModeValue}
        exitDrawModeCallback={exitFeatureDrawMode}
        geojson={layer.geojson!}
        isInEditMode={layer.isInEditMode || false}
        onNoFeatureClick={onNoFeatureClick}
        onViewOnlyFeatureClick={onViewOnlyFeatureClick}
        selectedFeatureIndex={layer.selectedFeatureIndex || 0}
        updateGeojson={updateFeature}
        viewOnlyStyle={style}
      />
    </>
  );
};
